import "./LocationWidget.scss"

import React, { useCallback, useEffect, useMemo } from 'react';
import { Collapse, Space, Typography, Button, Affix, Row, message } from 'antd';
import { ArrowUpOutlined, EnvironmentOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from "react-redux";
import { IAnalyticsAction, IApplicationState } from "../../store";

import * as LocationStore from '../../store/Location';
import ISite from "../../interfaces/ISite";
import { useTranslation } from "react-i18next";
import { GoogleTagManagerEvents } from '../../helpers/googleTagManager';
import NearbyVenueContent from "./NearbyVenueContent";
import VenueDetailBody from "./VenueDetailBody";
import AllVenueList from "./AllVenueList";
import ICoords from "../../interfaces/ICoords";
import { getCurrentDay } from "../../helpers/VenueDataFormatter";

const { Panel } = Collapse;

export interface IStickyInfoWidgetProps {
    affixTarget?: () => (Window | HTMLElement) | null;
    onWidgetHeightChange?: (height: number) => void;
    offsetTop?: number;
}

export const StickyInfoWidget: React.FC<IStickyInfoWidgetProps> = ({
    affixTarget,
    onWidgetHeightChange,
    offsetTop = 0,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const widgetRef = React.useRef<HTMLDivElement>(null);
    const selectedVenue = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const previousSelectedVenueRef = React.useRef<ISite | undefined>(undefined); //used to detect when some other part of the app selects a venue
    const location = useSelector((state: IApplicationState) => state.location.location);
    const locationFailed = useSelector((state: IApplicationState) => state.location.locationFailed);
    const previousLocation = React.useRef<ICoords | undefined>(undefined);

    const ViewNearbyVenuesBtnLabel = t('locationWidget.actions.viewNearbyLocations');
    const ViewAllVenuesBtnLabel = t('locationWidget.actions.viewAllLocations');
    const myVenueSetMsg = t('locationWidget.notifications.myVenueSet');
    const mainContainerId = "location-widget-content-box";

    const LocationPin = () => <EnvironmentOutlined className="location-icon" />;

    // when the location becomes available, assume it's because the user clicked 'find nearby venues' and wants to see that menu now
    React.useEffect(() => {
        const becameAvailable = !!location && !previousLocation.current;
        if (becameAvailable) {
            setNearbyVenueMode(true);
        }
        previousLocation.current = location;
    }, [location]);

    const [affixed, setAffixed] = React.useState(false);

    //track if the widget is expanded or collapsed
    const [open, setOpen] = React.useState(true);
    //need to let the collapse know if its open or not by setting which item to show. (eg item with key = 'panel')
    const activeKey = useMemo(() => {
        return open ? ['panel'] : [];
    }, [open]);
    //track if we want to lock the panel open (eg if no venue is selected)
    const lockOpen = useMemo(() => { return selectedVenue === undefined; }, [selectedVenue]);

    const [nearbyVenueMode, setNearbyVenueMode] = React.useState(false);

    // Handle affix state changes, to toggle between floating behaviour and standard layout behaviour
    const handleAffixChange = React.useCallback((isAffixed?: boolean) => {
        const newAffixed = isAffixed ?? false;
        setAffixed(newAffixed);
        
        if (onWidgetHeightChange && widgetRef.current) {
            if (!newAffixed) {
                // When becoming non-affixed, immediately report 0 height
                onWidgetHeightChange(0);
            } else {
                // When becoming affixed, report current height immediately
                // ResizeObserver will handle subsequent changes
                const height = widgetRef.current.offsetHeight;
                onWidgetHeightChange(height);
            }
        }
    }, [onWidgetHeightChange]);

    // ResizeObserver for reliable height tracking
    React.useEffect(() => {
        if (!widgetRef.current || !onWidgetHeightChange) return;

        let timeoutId: NodeJS.Timeout;
        let lastReportedHeight = 0;
        
        const resizeObserver = new ResizeObserver((entries) => {
            // Debounce updates to prevent excessive calls during animations
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                if (affixed && entries[0]) {
                    const height = affixed ? entries[0].contentRect.height : 0;
                    // Only update if height actually changed to prevent unnecessary rerenders
                    if (Math.abs(height - lastReportedHeight) > 1) {
                        lastReportedHeight = height;
                        onWidgetHeightChange(height);
                    }
                }
            }, 4);
        });

        resizeObserver.observe(widgetRef.current);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [affixed, onWidgetHeightChange]);


    function autoClosePopup() {
        // close after short delay to let the user see the venue details are in the widget
        setTimeout(() => setOpen(false), 600);
    }

    const handleSiteChange = (site: ISite) => {
        autoClosePopup();
        message.open({content:(
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'left', alignContent:'flex-start' }}>
                <div><EnvironmentOutlined /> {myVenueSetMsg}</div>
                <strong style={{ fontSize: '24px' }} >{site.name?.toUpperCase()}</strong>
            </div>
        )});
    };

    const handleCollapseToggle = (keys: string | string[]) => {
        const shouldBeOpen = Array.isArray(keys) ? keys.includes('panel') : keys === 'panel';

        if (lockOpen) {
            setOpen(true);
        }
        else {
            setOpen(shouldBeOpen);

            //if the user has clicked and toggled the ui, log an analytic event
            dispatch({
                type: 'ANALYTICS_EVENT',
                googleTagManagerEvent: GoogleTagManagerEvents.LocationWidget_toggleVenueDetails(shouldBeOpen),
            } as IAnalyticsAction);
        }

    };

    const clickShareLocationButton = () => {
        dispatch(LocationStore.actionCreators.postGetLocationAction());

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.ShareLocationClicked(),
        } as IAnalyticsAction);

        // use effect listening to location state will trigger swapping to nearby venue mode when location becomes available
    };


    

    const findAVenueHeader = (
        <Space size="small">
            <LocationPin />
            <Typography.Title level={2}>
                {t('locationWidget.titles.findAVenue')}
            </Typography.Title>
        </Space>
    );
    const shareLocationBody = (
        <Space direction="vertical" style={{ width: '100%', paddingTop:'10px' }}>
            <Typography.Text>{t('locationWidget.body.searchForNearest')}</Typography.Text>
            <Row>
                <Space wrap style={{paddingBottom:"30px"}} >
                    {locationFailed ?
                        <Typography.Text>{t('locationWidget.body.locationFailed')}</Typography.Text>
                        :
                        <Button type="primary" className="dark" onClick={clickShareLocationButton}>
                            <ArrowUpOutlined rotate={45} /> {t('locationWidget.actions.ShareLocation')?.toUpperCase()}
                        </Button>
                    }
                    </Space>
            </Row>
        </Space>
    );

    //top body is empty but picker should expand
    const pickNearbyVenueBody = (
        <div className="nearby-venue-list" style={{ backgroundColor: 'black' }}>
            {location && <NearbyVenueContent onSiteChange={handleSiteChange} />}
            <Button type="primary" className="outlinedBtn" id="allVenuesBtn" onClick={() => setNearbyVenueMode(false)} >{ViewAllVenuesBtnLabel}</Button>
        </div>
    );
    const allVenueListBody = (
        <div className="all-venue-list" style={{ backgroundColor: 'black' }}>
            {location && <Button type="primary" className="outlinedBtn" id="nearbyVenuesBtn" onClick={() => setNearbyVenueMode(true)} >{ViewNearbyVenuesBtnLabel}</Button>}
            <AllVenueList onSiteChange={handleSiteChange} parentContainerId={mainContainerId} />
        </div>
    );


    const venueHeaderContent = (
        <div style={{display:'flex'}} >
            <LocationPin />
            <div className="venue-header-content">
                <div className="venue-name-section">
                    <Typography.Title level={2}>
                        {selectedVenue?.name?.toUpperCase()}
                    </Typography.Title>
                </div>
                <div className="venue-open-times-section">
                    <Typography.Text>{t('statuses.today')?.toUpperCase()} : </Typography.Text>
                    <Typography.Text> {getCurrentDay(selectedVenue)}</Typography.Text>
                </div>
            </div>
        </div>
    );

    //switch between default (pick a location) vs venue (details of venue) heading and content
    let headingContent = <></>;
    let bodyContent = <></>;
    if (selectedVenue !== undefined) {
        headingContent = venueHeaderContent;
        bodyContent = <VenueDetailBody />;
    }
    else {
        headingContent = findAVenueHeader;
        if (nearbyVenueMode) {
            bodyContent = (<>
                {location ? <></> : shareLocationBody}
                {pickNearbyVenueBody}
            </>);
        }
        else {
            bodyContent = (<>
                {location ? <></> : shareLocationBody}
                {allVenueListBody}
            </>);
        }

        
    }

    //the expand icon chevron in the heading bar
    const expandIcon = useCallback(
        ({ isActive }) => (
            <RightOutlined rotate={isActive ? -90 : 90} style={{ transition: 'transform 0.2s' }} />
        ),
        []
    );

    useEffect(() => {
        const prev = previousSelectedVenueRef.current;
        const curr = selectedVenue;

        // close only on selection/change to a concrete site (not on clear)
        const becameSelected =
            (prev === undefined && curr !== undefined) ||
            (prev?.siteId !== undefined && curr?.siteId !== undefined && prev.siteId !== curr.siteId);

        if (becameSelected) {
            autoClosePopup();
        }

        previousSelectedVenueRef.current = curr;

    }, [selectedVenue]);

    return (
        <Affix target={affixTarget} offsetTop={offsetTop} onChange={handleAffixChange}>
            <div
                ref={widgetRef}
                id="locationWidgetContainer"
                role="region"
                aria-label="Page notice"
                className={`${affixed ? 'is-affixed' : ''} ${selectedVenue && open ? 'has-selected-venue' : ''}`}
            >
                <Collapse
                    className={`locationWidgetCollapse ${selectedVenue && open ? 'has-selected-venue' : ''}`}
                    bordered={false}
                    onChange={handleCollapseToggle}
                    activeKey={activeKey}
                    expandIcon={expandIcon}
                >
                    <Panel key="panel" header={headingContent} showArrow={!lockOpen}>
                        <div id={mainContainerId} >
                            {bodyContent}
                        </div>
                    </Panel>
                </Collapse>
            </div>

        </Affix>
    );
};
